// React Dependencies
import React, { useState, useEffect } from 'react';

// External Dependencies
import { connect } from 'react-redux';
import _ from 'lodash';
import PropTypes from 'prop-types';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { Chip, TextField } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { toast } from 'react-toastify';
import classNames from 'classnames';

// Internal Dependencies
import { Trim, EscapeSingleQuote, SortByAlphaNumericAndSymbols } from '../../../common/util';
import { IsEmpty } from '../../../common/checks';
import Constants from '../../../common/constants';
import PromoSubsidiary from './promoSubsidiary';
import PromoCreditCard from './promoCreditCard';
import MealsAvailableTag from './mealsAvailableTags';
import { getPaginatePages } from '../../../actions/admin/pageAction';
import { getPaginateTags } from '../../../actions/admin/tagAction';
import DeleteTagModal from './DeleteTagModal';
import apiService from '../../../services.js/apiService';
import MText from '../../common/MText';
import useDebounce from '../../global/use-debounce';
import Colours from '../../../styles/colours';

const useStyles = makeStyles((theme) => ({
  root: {
    '& > * + *': {
      marginTop: theme.spacing(2),
    },
  },
  textHeader: {
    marginLeft: theme.spacing(0.5),
  },
  approvedTag: {
    backgroundColor: '#2ecc71',
    color:           '#FFFFFF',
  },
  rejectedTag: {
    backgroundColor: '#e74c3c',
    color:           '#FFFFFF',
  },
  pendingTag: {
    backgroundColor: '#95a5a6',
    color:           '#FFFFFF',
  },
  popularTag: {
    background:   Colours.Purple,
    color:        '#FFFFFF',
    fontSize:     15,
    marginRight:  10,
    borderRadius: 10,
    padding:      '2px 12px',
    overflow:     'hidden',
    whiteSpace:   'nowrap',
    textOverflow: 'ellipsis',
    cursor:       'pointer',
    '&:hover':    {
      backgroundColor: Colours.Green1,
    },
  },
}));

const config = { Create: 'create-option', Select: 'select-option', Remove: 'remove-option', Clear: 'clear' };

// eslint-disable-next-line no-shadow, max-len
const PartnersTags = ({ userTags, postStatus, postId, page, getPaginatePages, getPaginateTags, promoPartners, promoSubsidiaries, promoCreditCards, onUserTagsChanged, mealsAvailFlag, onMealsAvailableChanged, onPromoPartnersChanged, onPromoSubsidiariesChanged, onPromoCreditCardsChanged }) => {
  const [currentUserTags, setCurrentUserTags] = useState(userTags);
  const [initial, setInitial] = useState([]);
  const [partners, setPartners] = useState([]);
  const [availablePartners, setAvailablePartners] = useState([]);
  const [tags, setTags] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [deleteAllTags, setDeleteAllTags] = useState(false);
  const [popularTags, setPopularTags] = useState(null);
  const [tagInput, setTagInput] = useState('');

  const debouncedTagInput = useDebounce(tagInput, 500);
  const classes = useStyles();

  const handleChangePartners = (event, values, reason) => {
    const selectedPartner = values[values.length - 1];
    setAvailablePartners([]);

    switch (reason) {
      case config.Create: {
        const haveIncluded = partners.filter((partner) => partner.brand.toLowerCase() === selectedPartner.toLowerCase());
        if (haveIncluded.length > 0) {
          return;
        }
        if (selectedPartner === page.name) {
          return;
        }
        const haveInitially = initial.filter((partner) => partner.brand.toLowerCase() === selectedPartner.toLowerCase());
        const haveEntity = availablePartners.filter((partner) => partner.name.toLowerCase() === selectedPartner.toLowerCase());
        if (haveInitially.length < 1) {
          if (haveEntity.length > 0) {
            const toAdd = {
              pageId: haveEntity[0].id,
              brand:  haveEntity[0].name,
            };
            setPartners([...partners].concat(toAdd));
            onPromoPartnersChanged([...partners].concat(toAdd));
          } else {
            setPartners([...partners].concat({ brand: selectedPartner }));
            onPromoPartnersChanged([...partners].concat({ brand: selectedPartner }));
          }
        } else {
          setPartners([...partners].concat(haveInitially[0]));
          onPromoPartnersChanged([...partners].concat(haveInitially[0]));
        }
        break;
      }
      case config.Select: {
        const checkInitial = initial.filter((partner) => partner.brand === selectedPartner.name);
        if (checkInitial.length < 1) {
          setPartners([...partners].concat({ pageId: selectedPartner.id, brand: selectedPartner.name }));
          onPromoPartnersChanged([...partners].concat({ pageId: selectedPartner.id, brand: selectedPartner.name }));
        } else {
          setPartners([...partners].concat(checkInitial[0]));
          onPromoPartnersChanged([...partners].concat(checkInitial[0]));
        }
        break;
      }
      case config.Remove: {
        const removedArr = _.difference([...partners], [...values]);
        const removedObj = removedArr[0];
        const newPartners = partners.filter((partner) => partner.brand !== removedObj.brand);
        setPartners(newPartners);
        onPromoPartnersChanged(newPartners);
        break;
      }
      case config.Clear: {
        setPartners([]);
        onPromoPartnersChanged([]);
        break;
      }
      default:
        return;
    }

    // handle to prevent in adding whitespace values to array.
    if (!IsEmpty(values) && IsEmpty(availablePartners)) {
      if (reason !== 'remove-option') {
        if (Trim(values[values.length - 1]).length < 1) {
          values.pop();
          setPartners(values);
          onPromoPartnersChanged(values);
        }
      }
    }
  };

  const handleChangeTags = (event, values, reason) => {
    const selectedTag = values[values.length - 1];
    switch (reason) {
      case config.Create: {
        let newTag = { name: selectedTag, isApproved: 'pending' };
        const checkDuplicate = tags.find((t) => t.name === selectedTag);
        if (!IsEmpty(checkDuplicate) || IsEmpty(selectedTag)) {
          return;
        }

        if (!IsEmpty(tags)) {
          if (allTags.find((t) => t.name === selectedTag)) {
            const existTag = allTags.find((t) => t.name === selectedTag);

            // remove suggestion from available tags too
            _.remove(availableTags.name, { name: selectedTag });

            existTag.isApproved = existTag.isApproved === true || existTag.isApproved === 'approved' ? 'approved' : existTag.isApproved === false || existTag.isApproved === 'rejected' ? 'rejected' : 'pending';
            newTag = existTag;
          }

          const deconstructTags = _.map(tags, 'name');
          setTags([...tags].concat(newTag));
          onUserTagsChanged([...deconstructTags].concat(selectedTag));
        } else {
          const deconstructTags = _.map(tags, 'name');
          setTags([...tags].concat(newTag));
          onUserTagsChanged([...deconstructTags].concat(selectedTag));
        }
        break;
      }
      case config.Select: {
        const checkInitial = tags.filter((tag) => tag.name === selectedTag);
        if (checkInitial.length < 1) {
          const deconstructTags = _.map(tags, 'name');

          setTags([...tags].concat(selectedTag));
          onUserTagsChanged([...deconstructTags].concat(selectedTag.name));
        } else {
          setTags([...tags].concat(checkInitial[0]));
          onUserTagsChanged([...tags].concat(checkInitial[0]));
        }
        break;
      }
      case config.Remove: {
        const extTags = tags.map((t) => t.name);
        const currentTags = values.map((t) => t.name);

        if (extTags.includes(_.toLower(page.name)) && !currentTags.includes(_.toLower(page.name))) {
          toast.error('Brand tag cannot be deleted');
        } else {
          const removedArr = _.difference([...tags], [...values]);
          const removedObj = removedArr[0];
          const newTags = tags.filter((tag) => tag !== removedObj);
          setTags(newTags);

          const deconstructTags = _.map(newTags, 'name');
          onUserTagsChanged(deconstructTags);
        }
        break;
      }
      case config.Clear: {
        setDeleteAllTags(true);
        break;
      }
      default:
    }

    // handle to prevent in adding whitespace values to array.
    if (!IsEmpty(values) && IsEmpty(availableTags)) {
      if (reason !== 'remove-option') {
        if (Trim(values[values.length - 1]).length < 1) {
          values.pop();
          setTags(values);
          onUserTagsChanged(values);
        }
      }
    }
  };

  const onDeleteAllTags = () => {
    const extTags = tags.map((t) => t.name);

    if (extTags.includes(_.toLower(page.name))) {
      setTags(tags.filter((t) => t.name === _.toLower(page.name)));
      onUserTagsChanged([_.toLower(page.name)]);
    } else {
      setTags([]);
      onUserTagsChanged([]);
    }

    setDeleteAllTags(false);
  };

  const formatTags = async (currentTags, fetchedTags) => {
    try {
      const formattedTags = [];
      if (!IsEmpty(currentTags)) {
        currentTags.forEach((v) => {
          if (_.some(fetchedTags, ['name', v])) {
            const tag = _.filter(fetchedTags, ['name', v]);
            let formatStatus = '';
            formatStatus = tag[0].isApproved === true ? 'approved' : tag[0].isApproved === false ? 'rejected' : 'pending';
            formattedTags.push({ name: tag[0].name, isApproved: formatStatus });
          } else if (!_.some(fetchedTags, ['name', v]) && !_.includes(currentTags, `"${v}"`)) {
            formattedTags.push({ name: v, isApproved: 'pending' });
          }
        });
        setTags(formattedTags);
      } else {
        setTags([]);
      }
    } catch (error) {
      setTags([]);
      toast.error(error);
    }
  };

  const getTags = async () => {
    try {
      const result = await apiService.getTagsStatus({ names: currentUserTags });
      if (result.totalCount > 0) {
        setAllTags(result.rows);
        formatTags(currentUserTags || [], result.rows);
      } else {
        formatTags(currentUserTags || [], []);
      }
    } catch (error) {
      toast.error(error);
    }
  };

  const getPopularTags = async () => {
    try {
      const result = await apiService.getCategorizedPopularTags();

      for (const key of Object.keys(result)) {
        result[key] = SortByAlphaNumericAndSymbols(result[key]);
      }

      setPopularTags(result);
    } catch (error) {
      toast.error(error);
    }
  };

  const addPopularTag = (tag) => {
    const existTag = tags.find((t) => t.name === tag);

    if (IsEmpty(existTag)) {
      const selectedTag = { name: tag, isApproved: 'approved' };
      const deconstructTags = _.map(tags, 'name');

      setTags([...tags].concat(selectedTag));
      onUserTagsChanged([...deconstructTags].concat(selectedTag.name));
    }
  };

  const onChange = async (e) => {
    const set = new Set();
    partners.forEach((partner) => set.add(partner.brand));
    if (e.target.value.length > 0) {
      const result = await getPaginatePages({ name: e.target.value, page: 0, pageSize: 5 });
      const availablePartnersTmp = [];
      result.rows.forEach((partner) => {
        if (!set.has(partner.name) && partner.name !== page.name) {
          availablePartnersTmp.push(partner);
        }
      });
      setAvailablePartners(availablePartnersTmp);
    } else {
      setAvailablePartners([]);
    }
  };

  useEffect(() => {
    const fetchTags = async () => {
      try {
        if (!IsEmpty(Trim(debouncedTagInput))) {
          const set = new Set();
          tags.forEach((tag) => set.add(tag.name));

          const nameSearch = EscapeSingleQuote(Trim(debouncedTagInput));
          const result = await getPaginateTags({ name: nameSearch, page: 0, pageSize: 10 });
          const tempAvailableTags = [];
          result.rows.forEach((tag) => {
            let tagStatus = '';
            if (!set.has(tag.name) && !tempAvailableTags.some((t) => t.name.toLowerCase().trim() === tag.name.toLowerCase().trim())) {
              tagStatus = tag.isApproved === true ? 'approved' : tag.isApproved === false ? 'rejected' : 'pending';
              tempAvailableTags.push({ name: tag.name, isApproved: tagStatus });
            }
          });
          setAvailableTags(tempAvailableTags);
        } else {
          setAvailableTags([]);
        }
      } catch (error) {
        toast.error(error);
      }
    };
    fetchTags();
  }, [debouncedTagInput, getPaginateTags]);

  const onBlurPartners = (value) => {
    const newPartner = Trim(value);
    if (availablePartners.length < 1) {
      if (!IsEmpty(newPartner)) {
        const mergePartners = _.concat(partners, { brand: newPartner });
        setPartners(mergePartners);
        onPromoPartnersChanged(mergePartners);
      }
    }
  };

  const handleBlurTags = (value) => {
    const selectedTag = Trim(value);
    let newTag = { name: selectedTag, isApproved: 'pending' };
    const checkDuplicate = tags.find((t) => t.name === selectedTag);

    if (!IsEmpty(checkDuplicate) || IsEmpty(selectedTag)) {
      return;
    }

    if (!IsEmpty(tags)) {
      if (allTags.find((t) => t.name === selectedTag)) {
        const existTag = allTags.find((t) => t.name === selectedTag);

        // remove suggestion from available tags too
        _.remove(availableTags.name, { name: selectedTag });

        existTag.isApproved = existTag.isApproved === true || existTag.isApproved === 'approved' ? 'approved' : existTag.isApproved === false || existTag.isApproved === 'rejected' ? 'rejected' : 'pending';
        newTag = existTag;
      }

      const deconstructTags = _.map(tags, 'name');
      setTags([...tags].concat(newTag));
      onUserTagsChanged([...deconstructTags].concat(selectedTag));
    } else {
      const deconstructTags = _.map(tags, 'name');
      setTags([...tags].concat(newTag));
      onUserTagsChanged([...deconstructTags].concat(selectedTag));
    }
  };

  const setSubsidiaryData = (data) => {
    onPromoSubsidiariesChanged(data);
  };

  const setCreditCardData = (data) => {
    onPromoCreditCardsChanged(data);
  };

  const updateTagsWithFNBTags = (data) => {
    const formattedTags = data.map((tagName) => _.assign({
      name:       tagName,
      isApproved: 'approved',
    }));

    const originalTags = _.filter(tags, (t) => !Constants.MealTags.includes(t.name));
    const mergedTags = _.uniqBy(originalTags.concat(formattedTags), (e) => e.name);

    const deconstructTags = mergedTags.map((tag) => tag.name);
    onUserTagsChanged(deconstructTags);
    setTags(mergedTags);
  };

  useEffect(() => {
    setInitial(promoPartners || []);
    setPartners(promoPartners || []);
    getTags();
    getPopularTags();
  }, []);

  useEffect(() => {
    setCurrentUserTags(userTags);
  }, [userTags]);

  return (
    <div className={classes.root}>
      {[Constants.PageType.FNB, Constants.PageType.Retailer, Constants.PageType.ECommerce].includes(page.type)
        && (
          <>
            <h5 className={classes.textHeader}>Meals Available</h5>
            <MealsAvailableTag
              postStatus={postStatus}
              tags={currentUserTags}
              updateTags={(data) => updateTagsWithFNBTags(data)}
              onSkipTag={(bool) => { onMealsAvailableChanged(bool); }}
              mealsAvailFlag={mealsAvailFlag}
            />
            <hr className="mt-3 pb-3" />
          </>
        )}

      <h5 className={classes.textHeader}>Tags</h5>

      {
        !IsEmpty(popularTags) && (
          <div className="w-100">
            {
              ['Others', ...Object.keys(popularTags).filter((item) => item !== 'Others')]
                .filter((key) => !IsEmpty(popularTags[key]))
                .map((category) => (
                  <div className="w-100 d-flex flex-column mb-2" key={category}>
                    {(category !== 'Others') && <MText variant="subtitle1" size={null} bold text={category} />}
                    <div className="w-100" style={{ display: 'ruby' }}>
                      {popularTags[category].map((popularTag, index) => (
                        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
                        <div
                          // eslint-disable-next-line react/no-array-index-key
                          key={`popularTag-${category}-${index}`}
                          className={classes.popularTag}
                          onClick={() => addPopularTag(popularTag)}
                        >
                          {popularTag}
                        </div>
                      ))}
                    </div>
                  </div>
                ))
            }
          </div>
        )
      }

      <Autocomplete
        multiple
        id="tags-filled"
        options={availableTags}
        getOptionLabel={(option) => option.name}
        value={tags}
        disableCloseOnSelect
        filterSelectedOptions
        freeSolo
        renderOption={(option) => (
          <>
            <span
              classes={{
                root: classNames(classes.tag, {
                  [classes.approvedTag]: option.isApproved === 'approved',
                  [classes.rejectedTag]: option.isApproved === 'rejected',
                  [classes.pendingTag]:  option.isApproved === 'pending',
                }),
              }}
              style={{
                background:
                  option.isApproved === 'approved' ? '#2ecc71'
                    : option.isApproved === 'rejected' ? '#e74c3c' : '#95a5a6',
                color:        '#FFFFFF',
                marginRight:  15,
                width:        10,
                height:       10,
                borderRadius: 10,
              }}
            />
            {option.name}
          </>
        )}
        renderTags={(value, getTagProps) => value.map((option, index) => (
          <Chip
            {...getTagProps({ index })}
            variant="outlined"
            label={option.name}
            classes={{
              root: classNames(classes.tag, {
                [classes.approvedTag]: option.isApproved === 'approved',
                [classes.rejectedTag]: option.isApproved === 'rejected',
                [classes.pendingTag]:  option.isApproved === 'pending',
              }),
            }}
            style={{ borderColor: '#FFFFFF', fontSize: 15 }}
          />
        ))}
        onChange={(event, values, reason) => handleChangeTags(event, values, reason)}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            onChange={(e) => setTagInput(IsEmpty(e.target.value) ? '' : e.target.value)}
            placeholder="Type and Enter to Submit Tags"
            onBlur={() => handleBlurTags(params.inputProps.value)}
          />
        )}
      />

      <hr className="mt-5 mb-5" />

      <h5 className={classes.textHeader}>Partners</h5>
      <Autocomplete
        multiple
        limitTags={5}
        id="tags-filled"
        options={availablePartners}
        getOptionLabel={(option) => option.name}
        disableCloseOnSelect={false}
        filterSelectedOptions
        freeSolo
        value={partners}
        renderTags={(value, getTagProps) => value.map((option, index) => (
          <Chip
            variant="outlined"
            label={option.brand}
            style={{ fontSize: 15 }}
            {...getTagProps({ index })}
          />
        ))}
        onChange={(event, values, reason) => handleChangePartners(event, values, reason)}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            onChange={(e) => onChange(e)}
            onBlur={() => onBlurPartners(params.inputProps.value)}
            placeholder="Type and Enter to Submit Custom Partner"
          />
        )}
      />

      <hr className="mt-5 mb-5" />

      {page.parentId === null
        && page.type !== Constants.PageType.CreditCard && (
        <div>
          <h5 className={classes.textHeader}>Subsidiaries</h5>
          <PromoSubsidiary postId={postId} page={page} promoSubsidiariesData={promoSubsidiaries} onSubsidiaryChanged={(e) => setSubsidiaryData(e)} />
          <hr className="mt-5 mb-5" />
        </div>
      )}

      { /** Display credit card selections only if the page is not a Credit Card type
       * or if it is a credit card parent page (i.e it is a Credit Card type but not parent)
       */}
      {(page.type !== Constants.PageType.CreditCard)
        || (page.type === Constants.PageType.CreditCard && page.parentId === null) ? (
          <div>
            <h5 className={classes.textHeader}>Credit Cards</h5>
            <PromoCreditCard postId={postId} page={page} promoCreditCardsData={promoCreditCards} onCreditCardChanged={(e) => setCreditCardData(e)} />
            <hr className="mt-5 mb-5" />
          </div>
        ) : ''}

      <DeleteTagModal
        open={deleteAllTags}
        onClose={() => setDeleteAllTags(false)}
        onConfirm={onDeleteAllTags}
      />
    </div>
  );
};

export default connect(null, { getPaginatePages, getPaginateTags })(PartnersTags);

PartnersTags.propTypes = {
  userTags:   PropTypes.arrayOf(PropTypes.any),
  postStatus: PropTypes.string,
  postId:     PropTypes.string,
  page:       PropTypes.objectOf(PropTypes.shape({
    name:     PropTypes.string,
    type:     PropTypes.string,
    parentId: PropTypes.string,
  })).isRequired,
  getPaginatePages:           PropTypes.func.isRequired,
  getPaginateTags:            PropTypes.func.isRequired,
  promoPartners:              PropTypes.arrayOf(PropTypes.any),
  promoSubsidiaries:          PropTypes.arrayOf(PropTypes.any),
  promoCreditCards:           PropTypes.arrayOf(PropTypes.any),
  onUserTagsChanged:          PropTypes.func.isRequired,
  mealsAvailFlag:             PropTypes.bool.isRequired,
  onMealsAvailableChanged:    PropTypes.func.isRequired,
  onPromoPartnersChanged:     PropTypes.func.isRequired,
  onPromoSubsidiariesChanged: PropTypes.func.isRequired,
  onPromoCreditCardsChanged:  PropTypes.func.isRequired,
};

PartnersTags.defaultProps = {
  userTags:          [],
  postStatus:        null,
  postId:            null,
  promoPartners:     [],
  promoSubsidiaries: [],
  promoCreditCards:  [],
};
